import { getAccountAgeInDays, type Permission } from '@teachino/shared-util';
import { isPaywallGatedFeature } from './paywall-gated-features';
import {
  findPersonalLegacyFreeLicense,
  getPaywallGraceDayCounts,
  hasPaidLicense,
  isInLegacyGracePeriod,
} from './paywall-grace-day-count';
import { isTrialExpiryGatedFeature } from './trial-expiry-gated-features';
import {
  getPersonalTrialDayCounts,
  type TrialLicense,
} from './trial-license-day-count';

/** Minimum account age before soft paywall banners appear (TE-2506). */
export const SOFT_PAYWALL_MIN_ACCOUNT_AGE_DAYS = 3;

export type SoftPaywallPeriod = 'grace' | 'trial';

export type SoftPaywallBannerState = {
  daysLeft: number;
  period: SoftPaywallPeriod;
};

export function canUseDuringSoftPaywall(
  licenses: TrialLicense[],
  _userCreatedAt: string,
): boolean {
  if (hasPaidLicense(licenses)) {
    return true;
  }

  if (getPersonalTrialDayCounts(licenses) !== null) {
    return true;
  }

  if (!findPersonalLegacyFreeLicense(licenses)) {
    return false;
  }

  const graceDayCounts = getPaywallGraceDayCounts();
  return graceDayCounts !== null;
}

/**
 * License permission or grace / trial soft-paywall access.
 * For TE-2506 paywall-gated features (student sharing, IPI), non-paid users
 * always use the soft-paywall period even when the trial license lists the feature.
 */
export function canUsePaywalledFeature(
  feature: Permission,
  hasFeaturePermission: boolean,
  licenses: TrialLicense[],
  userCreatedAt: string,
): boolean {
  if (hasPaidLicense(licenses)) {
    return hasFeaturePermission;
  }

  if (isPaywallGatedFeature(feature)) {
    return canUseDuringSoftPaywall(licenses, userCreatedAt);
  }

  if (hasFeaturePermission) {
    return true;
  }

  return canUseDuringSoftPaywall(licenses, userCreatedAt);
}

/**
 * Pro features on a stale trial license are blocked once the trial expires.
 * Legacy free users in the grace window retain full Pro access (TE-2506).
 */
export function canUseFeatureUnlessPersonalTrialExpired(
  hasFeaturePermission: boolean,
  licenses: TrialLicense[],
): boolean {
  if (hasPaidLicense(licenses)) {
    return hasFeaturePermission;
  }

  if (isInLegacyGracePeriod(licenses)) {
    return true;
  }

  if (!hasFeaturePermission) {
    return false;
  }

  return getPersonalTrialDayCounts(licenses) !== null;
}

/**
 * Whether a user may use a Pro feature, including grace / trial soft-paywall access.
 */
export function canUseProFeature(
  feature: Permission,
  hasFeaturePermission: boolean,
  licenses: TrialLicense[],
  userCreatedAt: string,
): boolean {
  if (hasPaidLicense(licenses)) {
    return hasFeaturePermission;
  }

  if (isInLegacyGracePeriod(licenses)) {
    return true;
  }

  if (isPaywallGatedFeature(feature)) {
    return canUsePaywalledFeature(
      feature,
      hasFeaturePermission,
      licenses,
      userCreatedAt,
    );
  }

  if (isTrialExpiryGatedFeature(feature)) {
    return canUseFeatureUnlessPersonalTrialExpired(
      hasFeaturePermission,
      licenses,
    );
  }

  return hasFeaturePermission;
}

export type UpsellBannerMode = 'soft' | 'hard';

export type UpsellBannerVisibility = {
  show: boolean;
  mode?: UpsellBannerMode;
  daysLeft?: number;
  period?: SoftPaywallPeriod;
};

/**
 * Single source of truth for upsell banner visibility and days-left messaging.
 * Paywall-gated features show soft banners during grace/trial even on trial licenses;
 * hard mode when soft access has ended (including disabled grace period).
 */
export function getUpsellBannerVisibility(
  feature: Permission,
  hasFeaturePermission: boolean,
  licenses: TrialLicense[],
  userCreatedAt: string,
): UpsellBannerVisibility {
  if (hasPaidLicense(licenses)) {
    return { show: false };
  }

  if (hasFeaturePermission && !isPaywallGatedFeature(feature)) {
    return { show: false };
  }

  const softBanner = getSoftPaywallBannerState(licenses, userCreatedAt);
  if (softBanner !== null) {
    return {
      show: true,
      mode: 'soft',
      daysLeft: softBanner.daysLeft,
      period: softBanner.period,
    };
  }

  if (!canUseDuringSoftPaywall(licenses, userCreatedAt)) {
    return { show: true, mode: 'hard' };
  }

  return { show: false };
}

export function getSoftPaywallBannerState(
  licenses: TrialLicense[],
  userCreatedAt: string,
): SoftPaywallBannerState | null {
  if (hasPaidLicense(licenses)) {
    return null;
  }

  const accountAgeDays = getAccountAgeInDays(userCreatedAt);
  if (accountAgeDays <= SOFT_PAYWALL_MIN_ACCOUNT_AGE_DAYS) {
    return null;
  }

  const trialDayCounts = getPersonalTrialDayCounts(licenses);
  if (trialDayCounts !== null) {
    return {
      daysLeft: trialDayCounts.trialDaysLeft,
      period: 'trial',
    };
  }

  if (!findPersonalLegacyFreeLicense(licenses)) {
    return null;
  }

  const graceDayCounts = getPaywallGraceDayCounts();
  if (!graceDayCounts) {
    return null;
  }

  return {
    daysLeft: graceDayCounts.graceDaysLeft,
    period: 'grace',
  };
}
